import type { AxiosAdapter, AxiosRequestConfig, AxiosResponse } from "axios";

import type { Notification } from "@/shared/api/notifications";

// ВАЖНО: не импортируем runtime enum NotificationType, чтобы избежать циклической зависимости
// mock-api -> notifications -> api -> mock-api. Используем локальные строковые константы.
const MOCK_NOTIFICATION_TYPE = {
  TOURNAMENT_INVITE: "tournament_invite",
  TEAM_INVITE: "team_invite",
  MATCH_STARTING: "match_starting",
  MATCH_RESULT: "match_result",
  TOURNAMENT_UPDATE: "tournament_update",
  DRAFT_STARTING: "draft_starting",
  SYSTEM: "system",
} as const;
import type {
  CreateOrganizationRequest,
  Organization,
  OrganizationMember,
} from "@/shared/api/organizations";
import type {
  CreateRosterRequest,
  Team,
  RosterInvitation,
} from "@/shared/api/roster";
import type {
  CreateTournamentRequest,
  Tournament,
  TournamentRegistration,
} from "@/shared/api/tournament";
import type {
  RiotAccount,
  UpdateUserRequest,
  UserProfile,
} from "@/shared/api/user";
import { TIME_INTERVALS, LOCAL_STORAGE_KEYS } from "@/shared/config";
import { handleMockChat } from "@/shared/lib/mock-chat";

// In-memory mock DB (per process). Reset on HMR.
// KAN-208: моки отдают то же, что админские ручки — это шире, чем публичные
// UserProfile/Organization. Раньше разница пряталась за `as any[]`.
type MockUser = UserProfile & {
  avatarUrl?: string;
  isMuted?: boolean;
  mutedUntil?: string | null;
  createdAt?: string;
  lastLoginAt?: string | null;
};

type MockOrganization = Organization & {
  ownerUsername?: string;
};

const db = {
  organizations: [] as MockOrganization[],
  membersByOrgId: new Map<string, OrganizationMember[]>(),
  users: [] as MockUser[],
  tournaments: [] as Tournament[],
  teams: [] as Team[],
  notifications: [] as Notification[],
  teamInvitations: [] as RosterInvitation[],
  tournamentRegistrations: [] as TournamentRegistration[],
  auditLogs: [] as Array<{
    id: string;
    userId: string;
    eventType: string;
    relatedId?: string;
    relatedType?: string;
    details?: string;
    createdAt: string;
    user?: {
      id: string;
      username?: string;
      displayName: string;
      avatarUrl?: string;
    } | null;
  }>,
};

// Seed minimal data
if (db.organizations.length === 0) {
  const orgId = crypto.randomUUID();
  const ownerUserId = crypto.randomUUID();
  const now = new Date().toISOString();

  // Organizations
  db.organizations.push({
    id: orgId,
    name: "GLT Esports",
    url: "glt",
    description: "Demo organization",
    isVerified: false,
    avatarUrl: "/avatars/default-avatar.svg",
    bannerUrl: "/covers/default-cover.svg",
    ownerId: ownerUserId,
    discordServerId: "",
    createdAt: now,
    memberCount: 2,
    tournamentCount: 0,
  });
  db.membersByOrgId.set(orgId, [
    {
      id: crypto.randomUUID(),
      organizationId: orgId,
      userId: ownerUserId,
      userName: "OwnerUser",
      userEmail: "owner@example.com",
      userAvatarUrl: "/avatars/default-avatar.svg",
      roleId: 1,
      roleName: "Owner",
      joinedAt: now,
      isOwner: true,
      isBanned: false,
      isStreamer: false,
    },
    {
      id: crypto.randomUUID(),
      organizationId: orgId,
      userId: crypto.randomUUID(),
      userName: "MemberUser",
      userEmail: "member@example.com",
      userAvatarUrl: "/avatars/default-avatar.svg",
      roleId: 3,
      roleName: "Member",
      joinedAt: now,
      isOwner: false,
      isBanned: false,
      isStreamer: false,
    },
  ]);

  // Achievements pool for mock users
  const achievementPool = [
    { icon: "Award", title: "Первый логин", description: "Зашёл на платформу" },
    {
      icon: "Award",
      title: "Заполнил профиль",
      description: "Добавил информацию о себе",
    },
    {
      icon: "Trophy",
      title: "Первая победа",
      description: "Выиграл свой первый матч",
    },
    {
      icon: "Shield",
      title: "Надёжный союзник",
      description: "Сыграл 10 матчей без ливов",
    },
    {
      icon: "Award",
      title: "Командный игрок",
      description: "Вступил в команду",
    },
    {
      icon: "Trophy",
      title: "В топе дня",
      description: "Попал в подборку лучших",
    },
    {
      icon: "Shield",
      title: "Фэр-плей",
      description: "Ни одного репорта за 30 дней",
    },
    { icon: "Award", title: "Настройщик", description: "Подключил соцсети" },
    {
      icon: "Trophy",
      title: "Серийная победа",
      description: "Серия из 5 побед",
    },
    {
      icon: "Award",
      title: "Аналитик",
      description: "Открыл статистику 20 раз",
    },
    { icon: "Shield", title: "Стойкость", description: "Сыграл 50 матчей" },
    {
      icon: "Award",
      title: "Коллекционер",
      description: "Добавил 3 любимых чемпионов",
    },
    { icon: "Trophy", title: "MVP матча", description: "Получил MVP" },
    {
      icon: "Award",
      title: "Исследователь",
      description: "Посетил 10 разных страниц",
    },
    {
      icon: "Shield",
      title: "Безупречность",
      description: "Без смертей в матче",
    },
    { icon: "Award", title: "Скаут", description: "Нашёл команду через поиск" },
    {
      icon: "Trophy",
      title: "Призёр турнира",
      description: "Топ-3 на турнире",
    },
    {
      icon: "Award",
      title: "Архитектор профиля",
      description: "Настроил все разделы профиля",
    },
    {
      icon: "Shield",
      title: "Железная воля",
      description: "Сделал 5 камбэков",
    },
    { icon: "Award", title: "Хроникёр", description: "Вернулся после 7 дней" },
  ];

  const pickAchievements = (min: number, max: number) => {
    const count = Math.floor(Math.random() * (max - min + 1)) + min;
    const shuffled = [...achievementPool].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, count);
  };

  // Users (seed primary)
  db.users.push({
    id: ownerUserId,
    username: "TheNotorious",
    displayName: "Алексей 'TheNotorious' Смирнов",
    isVerified: true,
    isAdmin: false,
    isSiteAdmin: true,
    platformRoles: ["SiteAdmin", "Developer"],
    primaryPlatformRole: "SiteAdmin",
    isOnline: true,
    status: "online",
    avatar: "/avatars/default-avatar.svg",
    banner: "/covers/default-cover.svg",
    about: "Профессиональный игрок в League of Legends",
    location: "Россия, Москва",
    languages: ["Русский", "English"],
    website: "https://thenotorious.gg",
    socialLinks: {
      x: "goodluck",
      discord: "goodluck#1234",
      youtube: "goodluck",
      telegram: "g-luck",
      twitch: "goodluck",
      steam: "goodluck",
      vk: "goodluck",
    },
    stats: {
      totalTournaments: 47,
      wins: 32,
      winrate: 68,
      kda: "4/2/7",
      rank: 15,
    },
    privacy: {
      showStats: true,
      showOnlineStatus: true,
      allowMessages: true,
      showInSearch: true,
    },
    notifications: {
      email: true,
      tournamentStart: true,
      newsAndRecommendations: false,
      discordEnabled: false,
      discordSendAll: false,
      discordTypes: [],
    },
    achievements: pickAchievements(6, 10),
    favoriteChampions: [
      { name: "Ясуо", image: "Yasuo", games: 15, winrate: 73 },
    ],
    championMasteries: [
      {
        championId: 157,
        championName: "Yasuo",
        championLevel: 7,
        championPoints: 287563,
        lastPlayTime: "2024-01-15T14:30:00Z",
      },
      {
        championId: 238,
        championName: "Zed",
        championLevel: 7,
        championPoints: 245123,
        lastPlayTime: "2024-01-14T16:45:00Z",
      },
      {
        championId: 91,
        championName: "Talon",
        championLevel: 6,
        championPoints: 198765,
        lastPlayTime: "2024-01-13T18:20:00Z",
      },
      {
        championId: 99,
        championName: "Lux",
        championLevel: 6,
        championPoints: 156432,
        lastPlayTime: "2024-01-12T12:15:00Z",
      },
      {
        championId: 103,
        championName: "Ahri",
        championLevel: 5,
        championPoints: 134567,
        lastPlayTime: "2024-01-11T19:30:00Z",
      },
    ],
    teams: [],
    riotAccounts: [
      {
        id: "riot1",
        gameName: "TheNotorious",
        tagLine: "RU1",
        region: "RU",
        verified: true,
        profileIconId: 4567,
        isMain: true,
        rank: "Diamond I",
        avatar:
          "https://ddragon.leagueoflegends.com/cdn/14.8.1/img/profileicon/4567.png",
      },
    ],
    streams: [
      {
        title: "Поднимаем ранг в Лиге Легенд!",
        viewers: 1245,
        game: "League of Legends",
        streamer: "TheNotorious",
        isOnline: true,
      },
    ],
  });

  // Seed additional demo users
  const seedUser = (
    username: string,
    displayName?: string,
    opts?: {
      status?: UserProfile["status"];
      primaryPlatformRole?: string;
      avatar?: string;
      banner?: string;
    },
  ) => {
    const primaryPlatformRole =
      opts?.primaryPlatformRole ??
      (username === "admin" ? "SiteAdmin" : "User");
    db.users.push({
      id: crypto.randomUUID(),
      username,
      displayName: displayName ?? username,
      isVerified: username === "admin",
      platformRoles: [primaryPlatformRole],
      primaryPlatformRole,
      isAdmin:
        primaryPlatformRole === "Moder" || primaryPlatformRole === "SiteAdmin",
      isSiteAdmin: username === "admin",
      isOnline:
        (opts?.status ?? "offline") === "online" ||
        (opts?.status ?? "offline") === "playing",
      status: opts?.status ?? "offline",
      avatar: opts?.avatar ?? "/avatars/default-avatar.svg",
      banner: opts?.banner ?? "/covers/default-cover.svg",
      about: "",
      location: "",
      languages: ["Русский"],
      website: "",
      socialLinks:
        username === "admin"
          ? {
              x: "glt_admin",
              discord: "GLTAdmin",
              youtube: "glt-admin",
              telegram: "glt_admin",
              twitch: "gltadmin",
              steam: "gltadmin",
              vk: "glt_admin",
            }
          : {},
      stats: {
        totalTournaments: Math.floor(Math.random() * 10),
        wins: Math.floor(Math.random() * 5),
        winrate: Math.floor(Math.random() * 100),
        kda: "2/2/4",
        rank: Math.floor(Math.random() * 1000),
      },
      privacy: {
        showStats: true,
        showOnlineStatus: true,
        allowMessages: true,
        showInSearch: true,
      },
      notifications: {
        email: true,
        tournamentStart: false,
        newsAndRecommendations: false,
        discordEnabled: false,
        discordSendAll: false,
        discordTypes: [],
      },
      achievements: pickAchievements(3, 8),
      favoriteChampions: [],
      championMasteries: [],
      teams: [],
      riotAccounts: [],
      streams: [],
    });
  };
  seedUser("admin", "Администратор", {
    status: "online",
    primaryPlatformRole: "SiteAdmin",
    avatar: "/avatars/da-logo.svg",
    banner: "/covers/org-cover.svg",
  });
  seedUser("mejaikin", "Mejaikin", {
    status: "playing",
    primaryPlatformRole: "Developer",
    avatar: "/avatars/default-avatar.svg",
    banner: "/covers/org-cover-2.svg",
  });
  seedUser("testuser", "Test User", { status: "offline" });
  seedUser("faker", "Faker", {
    status: "online",
    avatar: "/avatars/t1-logo.svg",
    banner: "/covers/org-cover.svg",
  });
  seedUser("chovy", "Chovy", {
    status: "away",
    avatar: "/avatars/g2-logo.svg",
    banner: "/covers/org-cover-2.svg",
  });
  seedUser("caps", "Caps", { status: "busy" });
  seedUser("rekkles", "Rekkles", {
    status: "offline",
    avatar: "/avatars/da-logo.svg",
  });
  seedUser("perkz", "Perkz", {
    status: "online",
    avatar: "/avatars/t1-logo.svg",
  });

  // Tournaments
  db.tournaments.push({
    id: "tour-1",
    name: "LoL Spring Championship 2024",
    description: "Весенний чемпионат по League of Legends",
    type: "classic",
    bracketType: "single-elimination",
    prizePool: 750000,
    createdById: ownerUserId,
    createdByName: "OwnerUser",
    startDate: new Date(Date.now() + TIME_INTERVALS.DAY * 10).toISOString(),
    endDate: new Date(Date.now() + TIME_INTERVALS.DAY * 17).toISOString(),
    maxPlayers: 160,
    status: "active",
    createdAt: now,
    registeredPlayersCount: 16,
    // required extra fields
    format: "5v5",
    game: "League of Legends",
    currency: "USD",
    location: "EU",
    currentTeams: 0,
    maxTeams: 32,
    image: "/covers/default-cover.svg",
    organizer: {
      id: orgId,
      name: "GLT Esports",
      avatar: "/avatars/default-avatar.svg",
    },
  });

  // Teams
  db.teams.push({
    id: "team-1",
    name: "T1",
    tag: "T1",
    icon: "/avatars/t1-logo.svg",
    banner: "/covers/default-cover.svg",
    game: "League of Legends",
    status: "active",
    tournament: "LoL World Championship 2023",
    tournamentId: "",
    captain: "Faker#T1",
    captainId: "captain-1",
    coach: "Kkoma",
    isTemporary: false,
    isLookingForPartner: false,
    tournaments: [
      {
        name: "LoL World Championship 2023",
        position: "1-е место",
        prize: 445000,
        year: "2023",
      },
    ],
    members: [
      {
        id: "1",
        username: "Zeus#T1",
        displayName: "Zeus#T1",
        role: "Top",
        avatarUrl: "/avatars/default-avatar.svg",
        isOnline: true,
        isCaptain: false,
        rank: "Challenger",
        region: "KR",
      },
    ],
    position: "1-е место",
    prize: 445000,
    matchHistory: [],
  });

  // Assign mock teams to users
  const sampleTeams = [
    {
      id: "team-1",
      name: "T1",
      icon: "/avatars/t1-logo.svg",
    },
    {
      id: "team-2",
      name: "G2",
      icon: "/avatars/g2-logo.svg",
    },
  ];
  // Create second team if missing
  if (!db.teams.find((t) => t.id === "team-2")) {
    db.teams.push({
      id: "team-2",
      name: "G2",
      tag: "G2",
      icon: "/avatars/g2-logo.svg",
      banner: "/covers/default-cover.svg",
      game: "League of Legends",
      status: "active",
      tournament: "",
      tournamentId: "",
      captain: "",
      captainId: "",
      coach: "",
      isTemporary: false,
      isLookingForPartner: false,
      tournaments: [],
      members: [],
      position: "",
      prize: 0,
      matchHistory: [],
    });
  }

  // Distribute teams across users
  db.users = db.users.map((u, idx) => {
    const teams: Array<{ id: string; name: string; members: unknown[] }> = [];
    const team1 = sampleTeams[idx % sampleTeams.length];
    const team2 = sampleTeams[(idx + 1) % sampleTeams.length];

    // Guard clause для team1
    if (team1) {
      teams.push({ id: team1.id, name: team1.name, members: [] });
    }

    // Guard clause для team2
    if (idx % 3 === 0 && team2) {
      teams.push({ id: team2.id, name: team2.name, members: [] });
    }

    return { ...u, teams } as typeof u;
  });

  // Notifications
  db.notifications.push({
    id: "notif-1",
    userId: "",
    type: MOCK_NOTIFICATION_TYPE.TOURNAMENT_INVITE as Notification["type"],
    title: "Новый турнир",
    message: "Доступна регистрация на LoL Spring Championship 2024",
    createdAt: new Date(now).toISOString(),
    isRead: false,
    data: { link: "/tournaments/tour-1" },
  });
}

function ok<T>(
  config: AxiosRequestConfig,
  data: T,
  status = 200,
): AxiosResponse<T> {
  return {
    config,
    data,
    status,
    statusText: String(status),
    headers: {},
    request: {},
  } as AxiosResponse<T>;
}

function notFound(
  config: AxiosRequestConfig,
  message = "Not Found",
): AxiosResponse<unknown> {
  return ok(config, { message }, 404);
}

function badRequest(
  config: AxiosRequestConfig,
  message = "Bad Request",
): AxiosResponse<unknown> {
  return ok(config, { message }, 400);
}

function getMockAdminUser() {
  const u = db.users[0];
  if (!u) {
    return {
      id: "mock_admin",
      username: "admin",
      displayName: "Админ (mock)",
      avatarUrl: "/avatars/default-avatar.svg",
    };
  }
  return {
    id: String(u.id ?? ""),
    username: u.username ?? undefined,
    displayName: String(u.displayName || u.username || u.id || ""),
    avatarUrl: u.avatarUrl || u.avatar || "/avatars/default-avatar.svg",
  };
}

function pushAdminAudit(
  details: string,
  relatedId?: string,
  relatedType?: string,
) {
  const admin = getMockAdminUser();
  db.auditLogs.unshift({
    id: crypto.randomUUID(),
    userId: admin.id,
    eventType: "AdminAction",
    relatedId,
    relatedType,
    details,
    createdAt: new Date().toISOString(),
    user: admin,
  });
}

export function createMockAxiosAdapter(): AxiosAdapter {
  return async (config: AxiosRequestConfig) => {
    const url = (config.url ?? "").replace(/^\/?api\/?/, "/");
    const path = url.split("?")[0] || url;
    const method = (config.method ?? "get").toLowerCase();

    // Helper to parse JSON data
    const parseData = () => {
      if (!config.data) return {};
      return typeof config.data === "string"
        ? JSON.parse(config.data)
        : config.data;
    };

    // KAN-1342: единый чат — мок с данными макета.
    const chat = handleMockChat(method, path, config.params as Record<string, unknown> | undefined, parseData() as Record<string, unknown>, db.users[0]?.id ?? "me");
    if (chat) return ok(config, chat.data, chat.status);

    if (
      method === "get" &&
      (url === "/organizations" || url === "/organizations/all")
    ) {
      const list = db.organizations.map((o) => ({
        ...o,
        memberCount: (db.membersByOrgId.get(o.id) ?? []).length,
      }));
      return ok(config, {
        organizations: list,
        pagination: {
          page: 1,
          limit: list.length,
          total: list.length,
          totalPages: 1,
        },
      });
    }

    // Get organization by id: GET /organizations/{id}
    const orgByIdMatch = url.match(/^\/organizations\/([^/]+)$/);
    if (method === "get" && orgByIdMatch) {
      const id = orgByIdMatch[1]!;
      const org = db.organizations.find((o) => o.id === id || o.url === id);
      if (!org) return notFound(config);
      return ok(config, {
        organization: {
          ...org,
          memberCount: (db.membersByOrgId.get(org.id) ?? []).length,
        },
      });
    }

    if (method === "post" && url === "/organizations") {
      const body = parseData() as CreateOrganizationRequest;
      if (!body.name || !body.name.trim())
        return badRequest(config, "Name required");
      const id = crypto.randomUUID();
      const now = new Date().toISOString();
      const created: Organization = {
        id,
        name: body.name.trim(),
        url: body.url?.trim() ?? "",
        description: body.description ?? "",
        isVerified: false,
        avatarUrl: "/avatars/default-avatar.svg",
        bannerUrl: "/covers/default-cover.svg",
        ownerId: crypto.randomUUID(),
        createdAt: now,
        memberCount: 0,
        discordServerId: "",
        tournamentCount: 0,
      };
      db.organizations.push(created);
      db.membersByOrgId.set(id, []);
      return ok(config, { organization: created }, 201);
    }

    // Members of organization: GET /organizations/{organizationId}/members
    const membersMatch = url.match(/^\/organizations\/([^/]+)\/members$/);
    if (method === "get" && membersMatch) {
      const orgId = membersMatch[1]!;
      const members = db.membersByOrgId.get(orgId) ?? [];
      return ok(config, members);
    }

    // Update member role: PUT /organizations/{organizationId}/members/{userId}/role
    const roleMatch = url.match(
      /^\/organizations\/([^/]+)\/members\/([^/]+)\/role$/,
    );
    if (method === "put" && roleMatch) {
      const orgId = roleMatch[1]!;
      const userId = roleMatch[2]!;
      const newRoleId = parseData();
      const members = db.membersByOrgId.get(orgId) ?? [];
      const idx = members.findIndex((m) => m.userId === userId);
      if (idx === -1) return notFound(config, "Member not found");
      const roleId =
        typeof newRoleId === "number" ? newRoleId : Number(newRoleId);
      const roleName =
        roleId === 1 ? "Owner" : roleId === 2 ? "Admin" : "Member";
      const currentMember = members[idx]!;
      members[idx] = { ...currentMember, roleId, roleName };
      db.membersByOrgId.set(orgId, members);
      return ok(config, members[idx]);
    }

    // Remove member: DELETE /organizations/{organizationId}/members/{userId}
    const delMatch = url.match(/^\/organizations\/([^/]+)\/members\/([^/]+)$/);
    if (method === "delete" && delMatch) {
      const orgId = delMatch[1]!;
      const userId = delMatch[2]!;
      const members = db.membersByOrgId.get(orgId) ?? [];
      const next = members.filter((m) => m.userId !== userId);
      db.membersByOrgId.set(orgId, next);
      return ok(config, {}, 204);
    }

    if (method === "post" && url === "/auth/login") {
      const data = parseData() as { username: string; password: string };

      // Проверяем тестовые аккаунты
      const testUsers = [
        { username: "admin", password: "password123", role: "admin" },
        { username: "mejaikin", password: "password123", role: "user" },
        { username: "testuser", password: "password123", role: "user" },
        { username: "faker", password: "password123", role: "user" },
        { username: "chovy", password: "password123", role: "user" },
      ];

      const testUser = testUsers.find(
        (u) => u.username === data.username && u.password === data.password,
      );
      if (!testUser) {
        return ok(
          config,
          {
            isSuccess: false,
            error: "Неверные учетные данные",
          },
          401,
        );
      }

      const mockToken = `dev_token_${testUser.username}_${Date.now()}`;
      const mockRefreshToken = `dev_refresh_${testUser.username}_${Date.now()}`;

      // Создаем мок профиль в соответствии с UserProfileSchema
      const mockProfile = {
        id: `dev_${testUser.username}`,
        username: testUser.username,
        displayName:
          testUser.username === "admin" ? "Администратор" : testUser.username,
        isVerified: testUser.role === "admin",
        role: testUser.role,
        isOnline: true,
        status: "online" as const,
        avatar: "",
        banner: "",
        about: testUser.role === "admin" ? "Главный админ" : "Пользователь",
        location: "Россия",
        languages: ["ru", "en"],
        website: "",
        isAdmin: testUser.role === "admin",
        isSiteAdmin: testUser.role === "admin",
        socialLinks:
          testUser.username === "admin"
            ? {
                x: "glt_admin",
                discord: "GLTAdmin",
                youtube: "glt-admin",
                telegram: "glt_admin",
                twitch: "gltadmin",
                steam: "gltadmin",
                vk: "glt_admin",
              }
            : {
                x: "",
                discord: "",
                youtube: "",
                telegram: "",
                twitch: "",
                steam: "",
                vk: "",
              },
        stats: {
          totalTournaments: Math.floor(Math.random() * 10),
          wins: Math.floor(Math.random() * 5),
          winrate: Math.floor(Math.random() * 100),
          kda: "2.5/1.2/4.1",
          rank: Math.floor(Math.random() * 1000),
        },
        privacy: {
          showStats: true,
          showOnlineStatus: true,
          allowMessages: true,
          showInSearch: true,
        },
        notifications: {
          email: true,
          tournamentStart: true,
          newsAndRecommendations: true,
        },
        achievements: [],
        favoriteChampions: [],
        championMasteries: [],
        teams: [],
        riotAccounts: [],
        streams: [],
      };

      // Обновляем/создаём пользователя в БД моков, чтобы /me возвращал актуальные соцсети
      const existingIdx = db.users.findIndex(
        (u) => u.username === mockProfile.username,
      );
      if (existingIdx >= 0) {
        db.users[existingIdx] = {
          ...db.users[existingIdx],
          ...mockProfile,
          socialLinks: {
            ...(db.users[existingIdx]?.socialLinks || {}),
            ...(mockProfile.socialLinks || {}),
          },
        } as MockUser;
      } else {
        db.users.push({ ...(mockProfile as MockUser) });
      }

      return ok(config, {
        isSuccess: true,
        token: mockToken,
        refreshToken: mockRefreshToken,
        user: mockProfile,
      });
    }

    if (method === "get" && url === "/me") {
      const preferred = db.users[0]?.username;
      const idx = preferred
        ? db.users.findIndex((u) => u.username === preferred)
        : 0;
      if (idx < 0 || !db.users[idx])
        return ok(config, { error: "Пользователь не найден" }, 404);
      // Fallback: гарантируем соцсети для admin
      if (db.users[idx]?.username === "admin") {
        const currentSocialLinks = db.users[idx]?.socialLinks || {};
        db.users[idx] = {
          ...db.users[idx],
          socialLinks: {
            x: currentSocialLinks.x || "glt_admin",
            discord: currentSocialLinks.discord || "GLTAdmin",
            youtube: currentSocialLinks.youtube || "glt-admin",
            telegram: currentSocialLinks.telegram || "glt_admin",
            twitch: currentSocialLinks.twitch || "gltadmin",
            steam: currentSocialLinks.steam || "gltadmin",
            vk: currentSocialLinks.vk || "glt_admin",
          },
        } as MockUser;
      }
      return ok(config, db.users[idx]);
    }

    // Get current user: GET /user/me (дублирующий эндпоинт для совместимости)
    if (method === "get" && url === "/user/me") {
      const user = db.users[0];
      if (!user) return ok(config, { error: "Пользователь не найден" }, 404);
      return ok(config, user);
    }
    if (method === "get" && url === "/user/profile") {
      const user = db.users[0]; // Return first user as current
      if (!user) return notFound(config, "User not found");
      return ok(config, user);
    }

    if (method === "put" && url === "/user/profile") {
      const data = parseData() as UpdateUserRequest;
      const user = db.users[0];
      if (!user) return notFound(config, "User not found");
      const updated = {
        ...user,
        ...data,
        socialLinks: { ...user.socialLinks, ...(data.socialLinks || {}) },
        privacy: { ...user.privacy, ...(data.privacy || {}) },
        notifications: { ...user.notifications, ...(data.notifications || {}) },
      } as typeof user;
      db.users[0] = updated;
      return ok(config, updated);
    }

    // Get user by ID: GET /user/{userId}
    const userByIdMatch = path.match(/^\/user\/([^/]+)$/);
    if (
      method === "get" &&
      userByIdMatch &&
      path !== "/user/search" &&
      path !== "/user/list" &&
      !path.startsWith("/user/username/")
    ) {
      const userId = userByIdMatch[1]!;
      const user = db.users.find(
        (u) =>
          u.id === userId ||
          u.username === userId ||
          `dev_${u.username}` === userId,
      );
      if (!user) return notFound(config, "User not found");
      return ok(config, { user });
    }

    // Search users: GET /user/search
    if (method === "get" && path === "/user/search") {
      const params = new URLSearchParams(url.split("?")[1] || "");
      const q = (params.get("q") || "").toLowerCase().trim();
      const limit = Number(params.get("limit") || 10);

      // Фаззи/косвенное совпадение: приоритизируем startsWith, затем includes
      const rank = (u: {
        username?: string | null;
        displayName?: string | null;
      }) => {
        const uname = String(u.username ?? "").toLowerCase();
        const dname = String(u.displayName ?? "").toLowerCase();
        if (!q) return 2;
        if (uname.startsWith(q) || dname.startsWith(q)) return 0;
        if (uname.includes(q) || dname.includes(q)) return 1;
        return 3;
      };

      let users = [...db.users];
      users.sort((a, b) => rank(a) - rank(b));
      if (q) users = users.filter((u) => rank(u) < 3);
      users = users.slice(0, isNaN(limit) ? 10 : limit);
      return ok(config, {
        users: users.map((u) => ({
          id: u.id,
          username: u.username,
          displayName: u.displayName,
          avatarUrl: u.avatar || "/avatars/default-avatar.svg",
          bannerUrl: u.banner || "/covers/default-cover.svg",
          status: u.status || "offline",
        })),
      });
    }

    // Users list: GET /user/list
    if (method === "get" && path === "/user/list") {
      const params = new URLSearchParams(url.split("?")[1] || "");
      const page = Math.max(1, Number(params.get("page") || 1));
      const size = Math.min(100, Math.max(1, Number(params.get("size") || 20)));
      const search = (params.get("search") || "").toLowerCase().trim();
      const status = (params.get("status") || "").toLowerCase().trim();
      const sortBy = (params.get("sortBy") || "CreatedAt").trim();
      const sortOrder = (params.get("sortOrder") || "desc")
        .toLowerCase()
        .trim();

      let users = [...db.users];

      if (search) {
        users = users.filter((u) => {
          const un = (u.username || "").toLowerCase();
          const dn = (u.displayName || "").toLowerCase();
          return un.includes(search) || dn.includes(search);
        });
      }

      if (status && status !== "all") {
        users = users.filter((u) => (u.status || "offline") === status);
      }

      if (sortBy === "DisplayName") {
        users.sort((a, b) =>
          String(a.displayName ?? "").localeCompare(
            String(b.displayName ?? ""),
          ),
        );
      } else if (sortBy === "Status") {
        const order: Record<string, number> = {
          online: 0,
          playing: 1,
          busy: 2,
          away: 3,
          offline: 4,
        };
        users.sort(
          (a, b) =>
            (order[a.status || "offline"] ?? 999) -
            (order[b.status || "offline"] ?? 999),
        );
      }

      if (sortOrder === "desc") users.reverse();

      const total = users.length;
      const totalPages = Math.max(1, Math.ceil(total / size));
      const start = (page - 1) * size;
      const pageUsers = users.slice(start, start + size);

      return ok(config, {
        users: pageUsers.map((u) => ({
          id: u.id,
          username: u.username,
          displayName: u.displayName,
          avatarUrl: u.avatar || "",
          bannerUrl: u.banner || "",
          status: u.status || "offline",
        })),
        total,
        page,
        size,
        totalPages,
      });
    }

    // Get user teams: GET /user/{userId}/teams
    const userTeamsMatch = path.match(/^\/user\/([^/]+)\/teams$/);
    if (method === "get" && userTeamsMatch) {
      const userId = userTeamsMatch[1]!;
      const user = db.users.find((u) => u.id === userId);
      if (!user) return notFound(config, "User not found");
      return ok(config, { teams: user.teams });
    }

    // Get user riot accounts: GET /user/{userId}/riot-accounts
    const userRiotMatch = path.match(/^\/user\/([^/]+)\/riot-accounts$/);
    if (method === "get" && userRiotMatch) {
      const userId = userRiotMatch[1]!;
      const user = db.users.find((u) => u.id === userId);
      if (!user) return notFound(config, "User not found");
      return ok(config, { accounts: user.riotAccounts });
    }

    // Add riot account: POST /user/riot-accounts
    if (method === "post" && url === "/user/riot-accounts") {
      const data = parseData() as {
        gameName: string;
        tagLine: string;
        region: string;
      };
      const allowedRegions = [
        "EUW",
        "EUNE",
        "NA",
        "BR",
        "LAN",
        "LAS",
        "OCE",
        "RU",
        "TR",
        "JP",
        "KR",
      ] as const;
      type Region = (typeof allowedRegions)[number];
      const region: Region = (allowedRegions as readonly string[]).includes(
        data.region,
      )
        ? (data.region as Region)
        : "EUW";
      const newAccount: RiotAccount = {
        id: crypto.randomUUID(),
        gameName: data.gameName,
        tagLine: data.tagLine,
        region,
        avatar: "/avatars/default-avatar.svg",
        verified: false,
        profileIconId: Math.floor(Math.random() * 10000),
        isMain: false,
        rank: "Без ранга",
      };
      const user = db.users[0];
      if (user) {
        user.riotAccounts ??= [];
        user.riotAccounts.push(newAccount);
      }
      return ok(config, { account: newAccount }, 201);
    }

    // Remove riot account: DELETE /user/riot-accounts/{accountId}
    const removeRiotMatch = url.match(/^\/user\/riot-accounts\/([^/]+)$/);
    if (method === "delete" && removeRiotMatch) {
      const accountId = removeRiotMatch[1]!;
      const user = db.users[0];
      if (user) {
        user.riotAccounts = (user.riotAccounts ?? []).filter(
          (acc) => acc.id !== accountId,
        );
      }
      return ok(config, {}, 204);
    }

    // Set main riot account: PUT /user/riot-accounts/{accountId}/main
    const setMainRiotMatch = url.match(
      /^\/user\/riot-accounts\/([^/]+)\/main$/,
    );
    if (method === "put" && setMainRiotMatch) {
      const accountId = setMainRiotMatch[1]!;
      const user = db.users[0];
      if (!user) return notFound(config, "User not found");
      user.riotAccounts ??= [];
      const account = user.riotAccounts.find((acc) => acc.id === accountId);
      if (!account) return notFound(config, "Account not found");
      user.riotAccounts.forEach((acc) => (acc.isMain = false));
      account.isMain = true;
      return ok(config, { account });
    }

    if (method === "get" && url.startsWith("/tournaments")) {
      const params = new URLSearchParams(url.split("?")[1] || "");
      let tournaments = [...db.tournaments];

      const statusParam = params.get("status");
      if (statusParam) {
        tournaments = tournaments.filter((t) => t.status === statusParam);
      }

      const page = Number(params.get("page") || 1);
      const limit = Number(params.get("limit") || tournaments.length || 10);
      const total = tournaments.length;
      const totalPages = Math.max(1, Math.ceil(total / (limit || 1)));
      const start = (Math.max(1, page) - 1) * (limit || total);
      const paged = tournaments.slice(start, start + (limit || total));

      return ok(config, {
        tournaments: paged,
        pagination: {
          page: Math.max(1, page),
          limit: limit || total,
          total,
          totalPages,
        },
      });
    }

    // Get tournament by ID: GET /tournaments/{tournamentId}
    const tournamentByIdMatch = url.match(/^\/tournaments\/([^/]+)$/);
    if (method === "get" && tournamentByIdMatch) {
      const tournamentId = tournamentByIdMatch[1]!;
      const tournament = db.tournaments.find((t) => t.id === tournamentId);
      if (!tournament) return notFound(config, "Tournament not found");
      return ok(config, { tournament });
    }

    if (method === "post" && url === "/tournaments") {
      const data = parseData() as CreateTournamentRequest;
      const newTournament: Tournament = {
        id: crypto.randomUUID(),
        name: data.name,
        description: data.description || "",
        type: data.type,
        bracketType: "SingleElimination",
        prizePool: data.prizePool,
        createdById: "user-1",
        createdByName: "Current User",
        startDate: data.startDate ?? "",
        endDate: data.endDate ?? "",
        maxPlayers: data.maxPlayers,
        status: "draft",
        createdAt: new Date().toISOString(),
        registeredPlayersCount: 0,
        format: data.type,
        // KAN-342: game/currency/location жили только в рукописной zod-схеме — в
        // бэкендовом DTO их нет и никогда не было. Мок держит те же константы, что
        // подставляет бэкенд, чтобы не изображать несуществующий контракт.
        game: "League of Legends",
        currency: "RUB",
        location: "Online",
        currentTeams: 0,
        maxTeams: data.maxPlayers,
        image: "/covers/default-cover.svg",
        organizer: {
          id: "org-1",
          name: "GLT Esports",
          avatar: "/avatars/default-avatar.svg",
        },
      };
      db.tournaments.push(newTournament);
      return ok(config, { tournament: newTournament }, 201);
    }

    // Register for tournament: POST /tournaments/{tournamentId}/register
    const registerMatch = url.match(/^\/tournaments\/([^/]+)\/register$/);
    if (method === "post" && registerMatch) {
      const tournamentId = registerMatch[1]!;
      const tournament = db.tournaments.find((t) => t.id === tournamentId);
      if (!tournament) return notFound(config, "Tournament not found");

      const userId = "user-1"; // Mock current user
      if (typeof tournament.registeredPlayersCount === "number") {
        tournament.registeredPlayersCount = Math.min(
          tournament.registeredPlayersCount + 1,
          tournament.maxPlayers ?? 0,
        );
      }

      const registration: TournamentRegistration = {
        tournamentId,
        userId,
        registeredAt: new Date().toISOString(),
      };
      db.tournamentRegistrations.push(registration);
      return ok(config, { success: true, registration }, 201);
    }

    if (method === "get" && url.startsWith("/teams")) {
      const params = new URLSearchParams(url.split("?")[1] || "");
      let teams = [...db.teams];

      if (params.get("game")) {
        teams = teams.filter((t) => t.game === params.get("game"));
      }
      if (params.get("status")) {
        teams = teams.filter((t) => t.status === params.get("status"));
      }

      const page = Number(params.get("page") || 1);
      const limit = Number(params.get("limit") || teams.length || 10);
      const total = teams.length;
      const totalPages = Math.max(1, Math.ceil(total / (limit || 1)));
      const start = (Math.max(1, page) - 1) * (limit || total);
      const paged = teams.slice(start, start + (limit || total));

      return ok(config, {
        teams: paged,
        pagination: {
          page: Math.max(1, page),
          limit: limit || total,
          total,
          totalPages,
        },
      });
    }

    // Get roster by ID: GET /rosters/{teamId}
    const teamByIdMatch = url.match(/^\/rosters\/([^/]+)$/);
    if (method === "get" && teamByIdMatch) {
      const teamId = teamByIdMatch[1]!;
      const team = db.teams.find((t) => t.id === teamId);
      if (!team) return notFound(config, "Roster not found");
      return ok(config, { team });
    }

    if (method === "post" && (url === "/rosters" || url === "/rosters/create")) {
      const data = parseData() as CreateRosterRequest;
      const newTeam: Team = {
        id: crypto.randomUUID(),
        name: data.name,
        tag: data.tag,
        icon: data.icon || "/avatars/default-avatar.svg",
        banner: data.banner || "/covers/default-cover.svg",
        game: (data as { game?: string }).game || "League of Legends",
        status: "active",
        tournament: "",
        tournamentId: "",
        captain: "Current User",
        captainId: "current_user_id",
        coach: "",
        isTemporary: false,
        isLookingForPartner: false,
        tournaments: [],
        members: [],
        position: "",
        prize: 0,
        matchHistory: [],
      };
      db.teams.push(newTeam);
      return ok(config, { team: newTeam }, 201);
    }

    if (
      method === "get" &&
      url.startsWith("/notifications") &&
      !url.includes("/unread-count")
    ) {
      // Проверяем авторизацию для уведомлений
      const token = localStorage.getItem(LOCAL_STORAGE_KEYS.TOKEN);
      if (!token || !token.startsWith("dev_token_")) {
        return ok(config, [], 200);
      }

      const params = new URLSearchParams(url.split("?")[1] || "");
      let notifications = [...db.notifications];

      if (params.get("type")) {
        notifications = notifications.filter(
          (n) => n.type === params.get("type"),
        );
      }
      if (params.get("isRead") !== null) {
        const isRead = params.get("isRead") === "true";
        notifications = notifications.filter((n) => n.isRead === isRead);
      }

      return ok(config, notifications);
    }

    // Mark notification as read: PUT /notifications/{notificationId}/read
    const markReadMatch = url.match(/^\/notifications\/([^/]+)\/read$/);
    if (method === "put" && markReadMatch) {
      const notificationId = markReadMatch[1];
      const notification = db.notifications.find(
        (n) => n.id === notificationId,
      );
      if (!notification) return notFound(config, "Notification not found");
      notification.isRead = true;
      return ok(config, {}, 204);
    }

    // Mark all notifications as read: PUT /notifications/read-all
    if (method === "put" && url === "/notifications/read-all") {
      db.notifications.forEach((n) => (n.isRead = true));
      return ok(config, {}, 204);
    }

    // Delete notification: DELETE /notifications/{notificationId}
    const deleteNotifMatch = url.match(/^\/notifications\/([^/]+)$/);
    if (method === "delete" && deleteNotifMatch) {
      const notificationId = deleteNotifMatch[1];
      const index = db.notifications.findIndex((n) => n.id === notificationId);
      if (index === -1) return notFound(config, "Notification not found");
      db.notifications.splice(index, 1);
      return ok(config, {}, 204);
    }

    // Clear all notifications: DELETE /notifications
    if (method === "delete" && url === "/notifications") {
      db.notifications.length = 0;
      return ok(config, {}, 204);
    }

    if (method === "get" && url === "/notifications/unread-count") {
      // Проверяем авторизацию для unread count
      const token = localStorage.getItem(LOCAL_STORAGE_KEYS.TOKEN);
      if (!token || !token.startsWith("dev_token_")) {
        return ok(config, { count: 0 }, 200);
      }

      const count = db.notifications.filter((n) => !n.isRead).length;
      return ok(config, { count });
    }

    // OAuth accounts: GET /auth/oauth/accounts
    if (method === "get" && url === "/auth/oauth/accounts") {
      return ok(config, []);
    }

    // OAuth URL: POST /auth/oauth/url
    if (method === "post" && url === "/auth/oauth/url") {
      const data = parseData();
      const { provider, redirectUri, state } = data;

      if (!provider || !redirectUri) {
        return badRequest(config, "Provider and redirectUri are required");
      }

      // Generate mock OAuth URL based on provider
      let oauthUrl = "";
      switch (provider.toLowerCase()) {
        case "discord":
          oauthUrl = `https://discord.com/api/oauth2/authorize?client_id=1275007419981893694&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=identify%20email%20guilds%20guilds.members.read`;
          break;
        case "google":
          oauthUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=471009242711-7tcthmhdeatst0rekfnj51q38vbtei8r.apps.googleusercontent.com&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=email%20profile`;
          break;
        case "twitch":
          oauthUrl = `https://id.twitch.tv/oauth2/authorize?client_id=buysd4259vaq793at0tm723sfmxvww&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=user:read:email`;
          break;
        default:
          return badRequest(config, `Unsupported provider: ${provider}`);
      }

      if (state) {
        oauthUrl += `&state=${encodeURIComponent(state)}`;
      }

      return ok(config, { url: oauthUrl });
    }

    // OAuth callback: POST /auth/oauth/callback
    if (method === "post" && url === "/auth/oauth/callback") {
      const data = parseData();
      const { provider, code, redirectUri } = data;

      if (!provider || !code || !redirectUri) {
        return badRequest(
          config,
          "Provider, code and redirectUri are required",
        );
      }

      // Generate mock token response
      const mockToken = `dev_token_${provider}_${Date.now()}`;
      const mockRefreshToken = `dev_refresh_${provider}_${Date.now()}`;

      return ok(config, {
        token: mockToken,
        refreshToken: mockRefreshToken,
        expiresIn: 3600,
        user: {
          id: `dev_user_${provider}`,
          username: `dev_${provider}_user`,
          displayName: `Dev ${provider} User`,
          email: `dev_${provider}@example.com`,
          role: "User",
          isVerified: true,
          isOnline: true,
          status: "online",
          avatar: "",
          banner: "",
          about: "",
          location: "",
          languages: ["ru", "en"],
          website: "",
          isAdmin: false,
          isSiteAdmin: false,
          socialLinks: {},
          stats: {
            totalTournaments: 0,
            wins: 0,
            winrate: 0,
            kda: "0/0/0",
            rank: 0,
          },
          privacy: {
            showStats: true,
            showOnlineStatus: true,
            allowMessages: true,
            showInSearch: true,
          },
          notifications: {
            email: true,
            tournamentStart: true,
            newsAndRecommendations: true,
          },
          achievements: [],
          favoriteChampions: [],
          championMasteries: [],
          teams: [],
          riotAccounts: [],
          streams: [],
        },
      });
    }

    if (method === "get" && path === "/admin/logs") {
      const params = new URLSearchParams(url.split("?")[1] || "");
      const page = Math.max(1, Number(params.get("page") || 1));
      const pageSize = Math.min(
        100,
        Math.max(1, Number(params.get("pageSize") || 20)),
      );
      const userId = (params.get("userId") || "").trim();

      let logs = [...db.auditLogs];
      if (userId) logs = logs.filter((l) => l.userId === userId);

      const totalCount = logs.length;
      const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
      const clampedPage = Math.min(page, totalPages);
      const start = (clampedPage - 1) * pageSize;
      const items = logs.slice(start, start + pageSize);

      return ok(config, {
        items,
        totalCount,
        pageNumber: clampedPage,
        pageSize,
        totalPages,
        hasPreviousPage: clampedPage > 1,
        hasNextPage: clampedPage < totalPages,
      });
    }

    // Admin users list: GET /admin/users
    if (method === "get" && path === "/admin/users") {
      const params = new URLSearchParams(url.split("?")[1] || "");
      const page = Math.max(1, Number(params.get("page") || 1));
      const pageSize = Math.min(
        100,
        Math.max(1, Number(params.get("pageSize") || params.get("size") || 20)),
      );
      const search = (params.get("search") || "").toLowerCase().trim();
      const isVerifiedParam = params.get("isVerified");
      const isVerified =
        isVerifiedParam === null ? undefined : isVerifiedParam === "true";

      let users = [...db.users];

      if (search) {
        users = users.filter((u) => {
          const un = String(u.username || "").toLowerCase();
          const dn = String(u.displayName || "").toLowerCase();
          const em = String(u.email || "").toLowerCase();
          return (
            un.includes(search) || dn.includes(search) || em.includes(search)
          );
        });
      }

      if (typeof isVerified === "boolean") {
        users = users.filter((u) => Boolean(u.isVerified) === isVerified);
      }

      const totalCount = users.length;
      const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
      const clampedPage = Math.min(page, totalPages);
      const start = (clampedPage - 1) * pageSize;
      const pageUsers = users.slice(start, start + pageSize);

      return ok(config, {
        items: pageUsers.map((u) => ({
          id: u.id,
          username: u.username,
          displayName: u.displayName,
          email: u.email,
          avatarUrl: u.avatar || u.avatarUrl || "/avatars/default-avatar.svg",
          roles: u.platformRoles || [],
          isVerified: Boolean(u.isVerified),
          isBanned: Boolean(u.isBanned),
          bannedAt: u.bannedAt,
          isMuted: Boolean(u.isMuted),
          mutedUntil: u.mutedUntil,
          createdAt: u.createdAt || new Date().toISOString(),
          lastLoginAt: u.lastLoginAt,
        })),
        totalCount,
        pageNumber: clampedPage,
        pageSize,
        totalPages,
        hasPreviousPage: clampedPage > 1,
        hasNextPage: clampedPage < totalPages,
      });
    }

    // Admin organizations list: GET /admin/organizations
    if (method === "get" && path === "/admin/organizations") {
      const params = new URLSearchParams(url.split("?")[1] || "");
      const page = Math.max(1, Number(params.get("page") || 1));
      const pageSize = Math.min(
        100,
        Math.max(1, Number(params.get("pageSize") || 20)),
      );
      const search = (params.get("search") || "").toLowerCase().trim();

      let orgs = [...db.organizations];
      if (search) {
        orgs = orgs.filter((o) => {
          const name = String(o.name || "").toLowerCase();
          const u = String(o.url || "").toLowerCase();
          return name.includes(search) || u.includes(search);
        });
      }

      const totalCount = orgs.length;
      const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
      const clampedPage = Math.min(page, totalPages);
      const start = (clampedPage - 1) * pageSize;
      const pageOrgs = orgs.slice(start, start + pageSize);

      return ok(config, {
        items: pageOrgs.map((o) => ({
          id: o.id,
          name: o.name,
          url: o.url,
          ownerId: o.ownerId,
          ownerUsername: o.ownerUsername,
          isVerified: Boolean(o.isVerified),
          memberCount: o.memberCount ?? 0,
          createdAt: o.createdAt,
        })),
        totalCount,
        pageNumber: clampedPage,
        pageSize,
        totalPages,
        hasPreviousPage: clampedPage > 1,
        hasNextPage: clampedPage < totalPages,
      });
    }

    // Admin organization verify: POST /admin/organizations/{id}/verify
    const adminOrgVerifyMatch = path.match(
      /^\/admin\/organizations\/([^/]+)\/verify$/,
    );
    if (method === "post" && adminOrgVerifyMatch) {
      const id = adminOrgVerifyMatch[1]!;
      const body = parseData() as { isVerified?: boolean };
      const org = db.organizations.find((o) => o.id === id);
      if (!org) return notFound(config, "Organization not found");
      org.isVerified = Boolean(body.isVerified);
      pushAdminAudit(
        org.isVerified
          ? "Выдана галочка организации"
          : "Снята галочка у организации",
        id,
        "Organization",
      );
      return ok(config, {});
    }

    // Admin organization delete: DELETE /admin/organizations/{id}
    const adminOrgDeleteMatch = path.match(/^\/admin\/organizations\/([^/]+)$/);
    if (method === "delete" && adminOrgDeleteMatch) {
      const id = adminOrgDeleteMatch[1]!;
      const idx = db.organizations.findIndex((o) => o.id === id);
      if (idx === -1) return notFound(config, "Organization not found");
      db.organizations.splice(idx, 1);
      pushAdminAudit("Удалена организация", id, "Organization");
      return ok(config, {}, 204);
    }

    // Admin verify: POST /admin/users/{userId}/verify
    const adminVerifyMatch = path.match(/^\/admin\/users\/([^/]+)\/verify$/);
    if (method === "post" && adminVerifyMatch) {
      const userId = adminVerifyMatch[1]!;
      const body = parseData() as { isVerified?: boolean };
      const user = db.users.find((u) => u.id === userId);
      if (!user) return notFound(config, "User not found");
      user.isVerified = Boolean(body.isVerified);
      pushAdminAudit(
        user.isVerified
          ? "Выдана галочка пользователю"
          : "Снята галочка у пользователя",
        userId,
        "User",
      );
      return ok(config, {});
    }

    // Admin ban/unban: POST /admin/users/{userId}/ban|unban
    const adminBanMatch = path.match(/^\/admin\/users\/([^/]+)\/(ban|unban)$/);
    if (method === "post" && adminBanMatch) {
      const userId = adminBanMatch[1]!;
      const action = adminBanMatch[2]!;
      const user = db.users.find((u) => u.id === userId);
      if (!user) return notFound(config, "User not found");
      if (action === "ban") {
        user.isBanned = true;
        user.bannedAt = new Date().toISOString();
        pushAdminAudit("Забанен пользователь", userId, "User");
      } else {
        user.isBanned = false;
        user.bannedAt = null;
        pushAdminAudit("Разбанен пользователь", userId, "User");
      }
      return ok(config, {});
    }

    // Admin mute/unmute: POST /admin/users/{userId}/mute|unmute
    const adminMuteMatch = path.match(
      /^\/admin\/users\/([^/]+)\/(mute|unmute)$/,
    );
    if (method === "post" && adminMuteMatch) {
      const userId = adminMuteMatch[1]!;
      const action = adminMuteMatch[2]!;
      const user = db.users.find((u) => u.id === userId);
      if (!user) return notFound(config, "User not found");
      if (action === "mute") {
        const body = parseData() as { durationMinutes?: number };
        const minutes = Math.max(1, Number(body.durationMinutes || 60));
        user.isMuted = true;
        user.mutedUntil = new Date(Date.now() + minutes * 60_000).toISOString();
        pushAdminAudit(
          `Замьючен пользователь на ${minutes} мин`,
          userId,
          "User",
        );
      } else {
        user.isMuted = false;
        user.mutedUntil = null;
        pushAdminAudit("Размьючен пользователь", userId, "User");
      }
      return ok(config, {});
    }

    // Admin force logout: POST /admin/users/{userId}/force-logout
    const adminForceLogoutMatch = path.match(
      /^\/admin\/users\/([^/]+)\/force-logout$/,
    );
    if (method === "post" && adminForceLogoutMatch) {
      const userId = adminForceLogoutMatch[1]!;
      pushAdminAudit("Принудительный выход пользователя", userId, "User");
      return ok(config, {});
    }

    // Fallback: 404
    return notFound(config);
  };
}
